import React, { forwardRef, ButtonHTMLAttributes } from 'react';
import { cn } from '../../utils/cn';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'solid' | 'outline' | 'red' | 'professional-red' | 'transparent' | 'underline';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'none';
  isLoading?: boolean;
  isActive?: boolean;
  children: React.ReactNode;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'solid',
      size = 'md',
      children,
      disabled,
      isLoading,
      isActive = false,
      ...props
    },
    ref
  ) => {
    const baseStyles =
      'inline-flex items-center justify-center font-normal transition-colors duration-200 !outline-none focus:outline-none active:outline-none !ring-0 focus:ring-0 active:ring-0 disabled:opacity-50 disabled:pointer-events-none';

    const getVariantStyles = () => {
      const variantStyles = {
        solid: 'border-0 bg-primary-600 text-white hover:bg-primary-700',
        outline: 'border-2 border-primary-600 text-primary-600 hover:bg-primary-50',
        red: 'border-0 bg-velux-professional-red text-velux-white hover:bg-velux-red',
        'professional-red':
          'border-0 bg-velux-professional-red text-velux-white hover:bg-velux-red',
        transparent: 'border-0 bg-transparent hover:bg-black/20',
        underline: isActive
          ? 'border-t-0 border-l-0 border-r-0 border-b-2 border-solid border-velux-red bg-transparent'
          : 'border-t-0 border-l-0 border-r-0 border-b-2 border-solid border-transparent bg-transparent hover:border-velux-grey',
      };

      return variantStyles[variant];
    };

    const sizes = {
      xs: 'px-2 py-1 text-xs h-8',
      sm: 'px-4 py-1.5 text-sm h-12',
      md: 'px-6 py-2 text-base h-14 lg:h-16',
      lg: 'px-8 py-3 text-lg h-16',
      none: '',
    };

    return (
      <button
        ref={ref}
        className={cn(baseStyles, getVariantStyles(), sizes[size], className)}
        disabled={disabled || isLoading}
        aria-disabled={disabled || isLoading}
        aria-busy={isLoading}
        {...props}
      >
        {isLoading ? (
          <svg
            className="animate-spin h-5 w-5"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        ) : (
          children
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';

export { Button };
